import useThemeStore from "@/store/useThemeStore";

export function useDashboardTheme() {
  const { theme } = useThemeStore();
  const isDark = theme === "dark";

  return {
    isDark,
    theme,
    colors: {
      bg: isDark ? "#0f0f1a" : "#F4F5F7",
      bgAlt: isDark ? "#0d0d1a" : "#E2E8F0",
      surface: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      surfaceAlt: isDark ? "rgba(255,255,255,0.02)" : "#F9FAFB",
      border: isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0",
      borderAlt: isDark ? "rgba(255,255,255,0.04)" : "#F1F5F9",
      text: isDark ? "#ffffff" : "#111827",
      textMuted: isDark ? "rgba(255,255,255,0.4)" : "#4B5563",
      textMutedLight: isDark ? "rgba(255,255,255,0.25)" : "#9CA3AF",
      divider: isDark ? "rgba(255,255,255,0.06)" : "rgba(20, 19, 26, 0.06)",
      inputBg: isDark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
      inputBorder: isDark ? "rgba(255,255,255,0.1)" : "#cbd5e1",
      inputText: isDark ? "#fff" : "#1f2937",
      tableHeaderBg: isDark ? "rgba(255,255,255,0.02)" : "#F3F4F6",
      rowHover: isDark ? "rgba(255,255,255,0.03)" : "rgba(108,99,255,0.03)",
      rowHoverAlt: isDark ? "rgba(108,99,255,0.07)" : "rgba(108,99,255,0.05)",
      cardBg: isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)"
        : "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
      topCardBg: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      topCardBorder: isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0",
      topCardText: isDark ? "#fff" : "#111827",
      badgeText: isDark ? "#fff" : "#111827",
      badgeBg: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      ringTrack: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      shimmerBg: isDark ? "rgba(255,255,255,0.02)" : "#E5E7EB",
      shimmerGrad: isDark
        ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.04), transparent)"
        : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.04), transparent)",
    }
  };
}

export function getBadgeTheme(type: string, isDark: boolean) {
  const darkMap: Record<string, { bg: string; color: string; label: string }> = {
    "Role Play":        { bg: "rgba(108,99,255,0.25)",  color: "#A78BFA", label: "Role Play" },
    "Meeting Uploaded": { bg: "rgba(6,182,212,0.2)",    color: "#67E8F9", label: "Meeting Uploaded" },
    "Quick Pitch":      { bg: "rgba(34,197,94,0.2)",    color: "#86EFAC", label: "Quick Pitch" },
    "Demo":             { bg: "rgba(251,191,36,0.2)",   color: "#FDE68A", label: "Demo" },
    "Script Completed": { bg: "rgba(249,115,22,0.2)",   color: "#FDBA74", label: "Script Completed" },
    "Negotiation":      { bg: "rgba(236,72,153,0.2)",   color: "#F9A8D4", label: "Negotiation" },
    "Pitch":            { bg: "rgba(34,197,94,0.2)",    color: "#86EFAC", label: "Pitch" },
    "Pitch Activity":   { bg: "rgba(34,197,94,0.2)",    color: "#86EFAC", label: "Pitch Activity" },
    "Digital Twin":     { bg: "rgba(167,139,250,0.2)",  color: "#C4B5FD", label: "Digital Twin" },
  };

  const lightMap: Record<string, { bg: string; color: string; label: string }> = {
    "Role Play":        { bg: "rgba(108,99,255,0.12)",  color: "#5B21B6", label: "Role Play" },
    "Meeting Uploaded": { bg: "rgba(6,182,212,0.12)",    color: "#0891B2", label: "Meeting Uploaded" },
    "Quick Pitch":      { bg: "rgba(34,197,94,0.12)",    color: "#15803D", label: "Quick Pitch" },
    "Demo":             { bg: "rgba(251,191,36,0.12)",   color: "#B45309", label: "Demo" },
    "Script Completed": { bg: "rgba(249,115,22,0.12)",   color: "#C2410C", label: "Script Completed" },
    "Negotiation":      { bg: "rgba(236,72,153,0.12)",   color: "#BE185D", label: "Negotiation" },
    "Pitch":            { bg: "rgba(34,197,94,0.12)",    color: "#15803D", label: "Pitch" },
    "Pitch Activity":   { bg: "rgba(34,197,94,0.12)",    color: "#15803D", label: "Pitch Activity" },
    "Digital Twin":     { bg: "rgba(167,139,250,0.12)",  color: "#6D28D9", label: "Digital Twin" },
  };

  const map = isDark ? darkMap : lightMap;
  return map[type] || { bg: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)", color: isDark ? "#fff" : "#111827", label: type };
}
